import { Connection } from '@solana/web3.js'

export type NetworkCluster = 'mainnet-beta' | 'devnet' | 'localnet'

const DEFAULT_ENDPOINTS: Record<NetworkCluster, string> = {
  'mainnet-beta': 'https://api.mainnet-beta.solana.com',
  'devnet': 'https://api.devnet.solana.com',
  'localnet': 'http://localhost:8899',
}

// MagicBlock Ephemeral Rollup endpoint (override with VITE_ER_ENDPOINT)
const DEFAULT_ER_ENDPOINT = 'https://devnet.magicblock.app'

function getEnvVar(key: string): string | null {
  const env = (import.meta as { env?: Record<string, string> }).env
  const val = env?.[key]
  return val && val.length > 0 ? val : null
}

/** Returns the RPC endpoint for the active cluster, respecting VITE_RPC_ENDPOINT override. */
function getRpcEndpoint(): string {
  return getEnvVar('VITE_RPC_ENDPOINT') ?? DEFAULT_ENDPOINTS[currentCluster]
}

let mainConnection: Connection | null = null
let erConnection: Connection | null = null
let currentCluster: NetworkCluster = 'devnet'

/**
 * Switch the active network cluster.
 * Clears cached connections so the next call to getConnection / getERConnection
 * creates a fresh Connection pointed at the new endpoint.
 */
export function setCluster(cluster: NetworkCluster): void {
  currentCluster = cluster
  mainConnection = null
  erConnection = null
}

/** Return the currently selected cluster. */
export function getCluster(): NetworkCluster {
  return currentCluster
}

/**
 * Get (or lazily create) the primary Solana connection for the active cluster.
 * Uses "confirmed" commitment level.
 */
export function getConnection(): Connection {
  if (!mainConnection) {
    mainConnection = new Connection(getRpcEndpoint(), 'confirmed')
  }
  return mainConnection
}

/**
 * Get (or lazily create) the MagicBlock Ephemeral Rollup connection.
 * Drill steps are sent here for sub-second latency.
 * In localnet mode, falls back to the main connection (no ER validator available).
 */
export function getERConnection(): Connection {
  if (currentCluster !== 'devnet') {
    throw new Error('Ephemeral Rollup is only supported on devnet')
  }
  const envEndpoint = getEnvVar('VITE_ER_ENDPOINT')
  const endpoint = envEndpoint ?? DEFAULT_ER_ENDPOINT
  if (!erConnection) {
    erConnection = new Connection(endpoint, 'confirmed')
  }
  return erConnection
}

/** Whether VRF is supported for the current cluster. */
export function isDevnet(): boolean {
  return currentCluster === 'devnet'
}
